import classNames from 'classnames';
import React from 'react';

import {
  exportAuditLogs,
  exportPlateHistoryReport,
  exportSampleContainerHistory,
  exportLocationHistoryReport,
} from 'audit/AuditActions';
import {
  EXPORT_AUDIT_LOG_DISPLAY_STRING,
  EXPORT_LOCATION_HISTORY_DISPLAY_STRING,
  EXPORT_PLATE_HISTORY_DISPLAY_STRING,
  EXPORT_SAMPLE_CONTAINER_HISTORY_DISPLAY_STRING,
  EXPORT_RESULT_AUDIT_LOG_DISPLAY_STRING,
} from 'audit/AuditConstants';
import ExportAuditLogButton from 'audit/ExportAuditLogButton';
import {ResultsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import licenses from 'user/Licenses';
import APIUtil from 'util/APIUtil';

export interface StorableExportButtonProps {
  storableId: string;
  buttonClassName?: string;
  buttonStyle?: ButtonStyle;
}

function getDropdownOptionsForEnterprise(storableId: string): Array<JSX.Element> {
  const options = [];

  if (APIUtil.isContainer(storableId)) {
    options.push(
      <Option
        onSelect={() => {
          exportSampleContainerHistory([storableId]);
        }}
        dataTestAttrs={{
          component: 'StorableExport',
          element: 'dropdownOption',
          key: 'exportContainerHistoryReport',
        }}
        key="exportContainerHistoryReport"
      >
        {EXPORT_SAMPLE_CONTAINER_HISTORY_DISPLAY_STRING}
      </Option>
    );
  } else if (APIUtil.isPlate(storableId)) {
    options.push(
      <Option
        onSelect={() => {
          exportPlateHistoryReport([storableId]);
        }}
        dataTestAttrs={{
          component: 'StorableExport',
          element: 'dropdownOption',
          key: 'exportPlateHistoryReport',
        }}
        key="exportPlateHistoryReport"
      >
        {EXPORT_PLATE_HISTORY_DISPLAY_STRING}
      </Option>
    );
  } else if (APIUtil.isLocation(storableId)) {
    options.push(
      <Option
        onSelect={() => {
          exportLocationHistoryReport([storableId]);
        }}
        dataTestAttrs={{
          component: 'StorableExport',
          element: 'dropdownOption',
          key: 'exportLocationHistoryReport',
        }}
        key="exportLocationHistoryReport"
      >
        {EXPORT_LOCATION_HISTORY_DISPLAY_STRING}
      </Option>
    );
  }

  // All storable types can export audit logs
  options.push(
    <Option
      onSelect={() => {
        exportAuditLogs([storableId]);
      }}
      dataTestAttrs={{
        component: 'StorableExport',
        element: 'dropdownOption',
        key: 'exportAuditLog',
      }}
      key="exportAuditLog"
    >
      {EXPORT_AUDIT_LOG_DISPLAY_STRING}
    </Option>
  );

  const canExportResultAuditLogs =
    isFeatureEnabled({feature: ResultsFeature.RESULTS_SHOW_DETAILS, item: {id: storableId}}) &&
    !APIUtil.isLocation(storableId);
  if (canExportResultAuditLogs) {
    options.push(
      <Option
        onSelect={() => {
          exportAuditLogs([], {relatedResultsModelId: storableId});
        }}
        dataTestAttrs={{
          component: 'StorableExport',
          element: 'dropdownOption',
          key: 'exportResultsAuditLog',
        }}
        key="exportResultsAuditLog"
      >
        {EXPORT_RESULT_AUDIT_LOG_DISPLAY_STRING}
      </Option>
    );
  }

  return options;
}

export default function StorableExportButton({
  storableId,
  buttonClassName,
  buttonStyle,
}: StorableExportButtonProps): JSX.Element {
  // This is shown in the storable's metadata tab and a grid view's sidebar
  const auditExportButtonStyle = buttonStyle || ButtonStyle.TERTIARY_BLUE;
  const options = licenses.hasEnterprisePlan() ? getDropdownOptionsForEnterprise(storableId) : [];
  const showDropdown = options.length > 1;

  return (
    <>
      {showDropdown ? (
        <Dropdown
          tooltip="Export report"
          menu={
            <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FIT_CONTENT} dropLeft={true}>
              {options}
            </Menu>
          }
        >
          <Button
            icon="share-v2"
            isDropdown={true}
            buttonStyle={auditExportButtonStyle}
            size={ButtonSize.SMALL}
            className={classNames('u-no-wrap', buttonClassName)}
            dataTestAttrs={{
              component: 'StorableExport',
              element: 'button',
              key: 'exportDropdownToggle',
            }}
          />
        </Dropdown>
      ) : (
        <ExportAuditLogButton
          buttonStyle={auditExportButtonStyle}
          ids={[storableId]}
          className="u-mini-spacing-left"
        />
      )}
    </>
  );
}
